import React, { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDataLayer from 'src/hooks/useDataLayer';
import usePrice from 'src/hooks/usePrice';

import { getImageBaseUrl } from 'helpers/request';
import { formatFittingTime } from 'helpers/utilities';

import Label from 'components/_utility/Label';

import { removeAccessory, increaseAccessoryQuantity, decreaseAccessoryQuantity } from 'src/redux/bike/actions';

import Wrench from 'src/assets/icons/wrench.svg';
import Minus from 'src/assets/icons/minus.svg';
import Plus from 'src/assets/icons/plus.svg';
import Cross from 'src/assets/icons/close.svg';
import TriumphTriangle from 'src/assets/icons/triumph-triangle.svg';

import 'objectFitPolyfill';
import { accessoryCategoriesSelector } from 'src/redux/configurator/selectors';
import {
  Wrap,
  ItemsContent,
  ItemsContentWrapper,
  PricingContainer,
  PricingInner,
  Name,
  Price,
  Swatch,
  SelectButton,
  InnerWrap,
  BikeImageWrap,
  FittingWrap,
  FittingContainer,
  FittingInner,
  AccessoryImage,
  QuantityWrap,
  QuantityButton,
  Quantity,
} from './BikeSummaryItem.styled';
import cloudinary from 'helpers/cloudinary';

const ItemWrapper = ({
  swatchImage,
  name,
  price,
  priceLocalised,
  modelPrice = 0,
  selected,
  fittingTime,
  image,
  id,
  quantity,
  isAccessories,
  isColour,
  bikeImage,
  allowMultiple,
  categoryId,
  partNumber,
  showAllColours,
  hasCTA,
  ctaAction,
  ctaLabel,
}) => {
  const dispatch = useDispatch();
  const formatPrice = usePrice();
  const pushToDataLayer = useDataLayer();

  const accessoryCategories = useSelector(accessoryCategoriesSelector);
  const accessoryCategory = categoryId && accessoryCategories.find(category => category.id === categoryId);

  useEffect(() => {
    window.objectFitPolyfill();
  }, []);

  const accessoryDimensions = {
    configuratorAccessoryName: name,
    configuratorAccessoryCategory: (accessoryCategory || {}).name,
    configuratorAccessoryID: partNumber,
    configuratorAccessoryPrice: price || priceLocalised,
  };

  return (
    <Wrap>
      <InnerWrap isAccessories={isAccessories} allowMultiple={allowMultiple}>
        {swatchImage ? <Swatch swatchImage={getImageBaseUrl() + swatchImage} /> : null}
        {image || isAccessories ? (
          <BikeImageWrap>
            {image ? (
              <AccessoryImage backgroundImage={cloudinary(image.src, 'c_thumb,w_180')} />
            ) : (
              <AccessoryImage>
                <TriumphTriangle />
              </AccessoryImage>
            )}
          </BikeImageWrap>
        ) : null}
        <ItemsContentWrapper>
          <ItemsContent isAccessories={isAccessories}>
            <Name dangerouslySetInnerHTML={{ __html: name }} />
          </ItemsContent>
          {isColour ? (
            <Fragment>
              {price === parseInt(modelPrice, 10) ? (
                <PricingContainer showAllColours={showAllColours}>
                  <PricingInner>
                    <Price>
                      <Label name="includedAsStandardLabel" />
                    </Price>
                  </PricingInner>
                </PricingContainer>
              ) : (
                <PricingContainer showAllColours={showAllColours}>
                  <PricingInner>
                    <Price>{formatPrice(price - modelPrice)}</Price>
                  </PricingInner>
                </PricingContainer>
              )}
            </Fragment>
          ) : null}
          {bikeImage ? (
            <PricingContainer>
              <PricingInner>
                <Price>{formatPrice(price)}</Price>
              </PricingInner>
            </PricingContainer>
          ) : null}
          {((!isColour && price && price !== parseInt(modelPrice, 10)) || fittingTime || priceLocalised) &&
          !bikeImage ? (
            <FittingWrap isAccessories={isAccessories}>
              {(price || priceLocalised) && !isColour ? (
                <FittingContainer>
                  <FittingInner>
                    <Price>{price ? formatPrice(price) : priceLocalised}</Price>
                  </FittingInner>
                </FittingContainer>
              ) : null}
              {typeof fittingTime !== 'undefined' ? (
                <FittingContainer fittingTime>
                  <FittingInner fittingTime>
                    <Wrench />
                    <Price>{formatFittingTime(fittingTime)}</Price>
                  </FittingInner>
                </FittingContainer>
              ) : null}
            </FittingWrap>
          ) : null}
        </ItemsContentWrapper>
        {isAccessories && allowMultiple ? (
          <QuantityWrap allowMultiple={allowMultiple}>
            <QuantityButton
              onClick={() => [
                dispatch(quantity === 1 ? removeAccessory(id) : decreaseAccessoryQuantity(id)),
                pushToDataLayer('configuratorAccessoryRemoved', accessoryDimensions),
              ]}
              data-testid="decreaseQuantity"
            >
              <Minus />
            </QuantityButton>
            <Quantity>{quantity}</Quantity>
            <QuantityButton
              onClick={() => [
                dispatch(increaseAccessoryQuantity(id)),
                pushToDataLayer('configuratorAccessorySelected', accessoryDimensions),
              ]}
              data-testid="increaseQuantity"
              disabled={quantity >= 9}
            >
              <Plus />
            </QuantityButton>
          </QuantityWrap>
        ) : null}
        {isAccessories && !allowMultiple ? (
          <QuantityWrap>
            <Cross
              onClick={() => [
                dispatch(removeAccessory(id)),
                pushToDataLayer('configuratorAccessoryRemoved', accessoryDimensions),
              ]}
            />
          </QuantityWrap>
        ) : null}
      </InnerWrap>
      {hasCTA && ctaLabel ? (
        <SelectButton selected={selected} onClick={ctaAction}>
          <Label name={ctaLabel} />
        </SelectButton>
      ) : null}
    </Wrap>
  );
};

export default ItemWrapper;
